import { zodResolver } from "@hookform/resolvers/zod"
import { DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import type { ApprovalStatus, PriorityStatus } from "@/graphql/codegen/graphql"
import { LocationRowFields } from "@/screens/CustomerProducts/components/AssociateProductDialog/LocationRowFields"
import { useUpdateAssociationLocation } from "@/screens/CustomerProducts/hooks/useUpdateAssociationLocation"
import {
  type AssociationLocationFormValues,
  createAssociationLocationFormSchema,
} from "@/screens/Products/schemas/associateProductForm"
import type { Option } from "@/types/form-fields"

export interface AssociationLocationEditProps {
  companyProductAssociationLocationId: string
  shipToLocationId: string
  shipToLocationName: string
  customerCodes: string[]
  priorityStatus?: PriorityStatus | null
  approvalStatus?: ApprovalStatus | null
}

interface EditAssociationLocationContentProps {
  location: AssociationLocationEditProps
  customerCodes: string[]
  onClose: () => void
  onSuccess: () => void
}

export const EditAssociationLocationContent: FC<EditAssociationLocationContentProps> = ({
  location,
  customerCodes,
  onClose,
  onSuccess,
}) => {
  const schema = useMemo(() => createAssociationLocationFormSchema([]), [])

  const methods = useForm<AssociationLocationFormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      locations: [
        {
          shipToLocationId: location.shipToLocationId,
          customerCode: location.customerCodes,
          priorityStatus: location.priorityStatus,
          approvalStatus: location.approvalStatus,
        },
      ],
    },
  })

  const {
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const locationOptions = useMemo<Option<string>[]>(
    () => [{ value: location.shipToLocationId, label: location.shipToLocationName }],
    [location.shipToLocationId, location.shipToLocationName]
  )

  const { updateLocation, isUpdating } = useUpdateAssociationLocation()

  const canSubmit = isValid && isDirty && !isSubmitting && !isUpdating

  const handleSave = methods.handleSubmit(async (formValues: AssociationLocationFormValues) => {
    const loc = formValues.locations[0]
    if (!loc) return

    const success = await updateLocation({
      id: location.companyProductAssociationLocationId,
      customerCode: loc.customerCode?.length ? loc.customerCode : undefined,
      priorityStatus: loc.priorityStatus ?? undefined,
      approvalStatus: loc.approvalStatus ?? undefined,
    })

    if (success) {
      onSuccess()
    }
  })

  return (
    <>
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationRowFields index={0} locationOptions={locationOptions} customerCodes={customerCodes} disableShipTo />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button appearance='contained' onClick={handleSave} disabled={!canSubmit}>
          Save
        </Button>
      </DialogActions>
    </>
  )
}
